import { useEffect, useState, useMemo, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { idFromSlug, recipePath } from "@/lib/slug";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import CopyToCookbookButton from "@/components/CopyToCookbookButton";
import HeartButton from "@/components/HeartButton";
import { useVoiceSearch } from "@/contexts/VoiceSearchContext";
import AddToShoppingListDrawer from "@/components/AddToShoppingListDrawer";
import AddToPlanButton from "@/components/AddToPlanButton";
import { toast } from "sonner";
import { QRCodeCanvas } from "qrcode.react";
import { speak, warmUpVoices } from "@/lib/voice";
import {
  ForkKnife, Clock, Users, Star, Printer, Share, ArrowRight,
  Microphone, MicrophoneSlash, Sparkle, Copy as CopyIcon, QrCode, PencilSimple,
  DownloadSimple, ShareNetwork, SpeakerHigh, Swap, Heart, Trash, UploadSimple, Play, Books, BookOpenText,
  Image as ImageIcon, Record, Stop, X as CloseIcon, Camera,
} from "@phosphor-icons/react";
import PrintImageGuardModal from "@/components/PrintImageGuardModal";
import PageMeta from "@/components/PageMeta";
import { SsrFallback, hasSsr } from "@/lib/ssrSnapshot";
import RecipeJsonLd from "@/components/RecipeJsonLd";
import { buildRecipeMetaDescription } from "@/lib/seoText";
import ShareRecipeButton from "@/components/ShareRecipeButton";
import CookLevelBadge from "@/components/CookLevelBadge";
import RecipeSummaryCard from "@/components/RecipeSummaryCard";
import TranslateCard from "@/components/TranslateCard";
import NutritionCard from "@/components/NutritionCard";
import DietBadges from "@/components/DietBadges";
import { useSiteFlags } from "@/contexts/SiteFlagsContext";
import { useKitchenHelpIndex, KitchenHelpLinkify } from "@/lib/kitchenHelp";

function parseTimers(text) {
  const re = /(\d+)\s*(min(?:ute)?s?|hour|hrs?|h)\b/gi;
  const parts = [];
  let lastIdx = 0; let m;
  while ((m = re.exec(text)) !== null) {
    if (m.index > lastIdx) parts.push({ kind: "text", value: text.slice(lastIdx, m.index) });
    const n = parseInt(m[1]);
    const unit = m[2].toLowerCase();
    const seconds = /hour|hr|h/.test(unit) ? n * 3600 : n * 60;
    parts.push({ kind: "timer", label: `${n} ${unit}`, seconds });
    lastIdx = m.index + m[0].length;
  }
  if (lastIdx < text.length) parts.push({ kind: "text", value: text.slice(lastIdx) });
  return parts;
}

// Browser-native beep (Web Audio API, no asset required)
function playBeep(times = 3) {
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    const ctx = new AC();
    let t = ctx.currentTime;
    for (let i = 0; i < times; i++) {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain); gain.connect(ctx.destination);
      osc.type = "sine";
      osc.frequency.setValueAtTime(880, t);
      gain.gain.setValueAtTime(0, t);
      gain.gain.linearRampToValueAtTime(0.25, t + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.4);
      osc.start(t);
      osc.stop(t + 0.45);
      t += 0.55;
    }
    setTimeout(() => { try { ctx.close(); } catch {} }, 2500);
  } catch {}
}

function TimerChip({ label, seconds, onLaunch }) {
  return (
    <button onClick={() => onLaunch(label, seconds)} className="inline-flex items-center gap-1 mx-1 px-2.5 py-1 rounded-full bg-rf-amber/20 text-rf-orange text-sm font-bold hover:bg-rf-amber/40 transition-colors">
      <Clock size={14} weight="bold" /> {label}
    </button>
  );
}

export default function RecipeView() {
  const { id: slugId } = useParams();
  const id = idFromSlug(slugId);
  const { user } = useAuth();
  const { flags } = useSiteFlags();
  const nav = useNavigate();
  const [recipe, setRecipe] = useState(null);
  const { setVoiceRecipe } = useVoiceSearch();
  useEffect(() => { setVoiceRecipe(recipe); return () => setVoiceRecipe(null); }, [recipe, setVoiceRecipe]);
  // loadError distinguishes "still loading" (null + null) from "fetch failed"
  // (null + set). Prior UI showed `Loading…` forever on a failed fetch, which
  // on iOS Safari looked like a "blank white screen" — the real symptom
  // behind the post-save bug report on 2026-07-06.
  const [loadError, setLoadError] = useState(null);
  const [scale, setScale] = useState(1);
  const [overrideServes, setOverrideServes] = useState(null);
  const [timers, setTimers] = useState([]); // { id, label, remaining, total }
  const [pairings, setPairings] = useState(null);
  const [pairingsLoading, setPairingsLoading] = useState(false);
  const [voiceOn, setVoiceOn] = useState(false);
  const [stepIdx, setStepIdx] = useState(0);
  const [ttsOn, setTtsOn] = useState(false);
  const [cookbooks, setCookbooks] = useState([]);
  const [copyOpen, setCopyOpen] = useState(false);
  const [moveOpen, setMoveOpen] = useState(false);
  const [showQR, setShowQR] = useState(false);
  const [printGuardOpen, setPrintGuardOpen] = useState(false);
  const [note, setNote] = useState("");
  const [subOpen, setSubOpen] = useState(null); // ingredient name
  const [subData, setSubData] = useState(null);
  const [subLoading, setSubLoading] = useState(false);
  const khIndex = useKitchenHelpIndex();
  // SuperAdmin quick-action on image-less recipes — kicks off the same
  // async AI headline-image job as the recipe editor.
  const [genImgLoading, setGenImgLoading] = useState(false);
  const [audioBusy, setAudioBusy] = useState(false);
  const audioFileRef = useRef(null);
  // Live recording state — uses MediaRecorder API for in-app voice memories.
  // Hard-capped at 60 seconds so memories stay snackable and small to upload.
  const [recording, setRecording] = useState(false);
  const [recordSeconds, setRecordSeconds] = useState(0);
  const mediaRecRef = useRef(null);
  const recChunksRef = useRef([]);
  const recTimerRef = useRef(null);
  // Scan-page lightbox so users can zoom in on grandma's handwriting and
  // promote any scan to the recipe's hero image with one click.
  const [scanModalIdx, setScanModalIdx] = useState(null);
  const [scanBusy, setScanBusy] = useState(false);
  // Prev / next sibling within the same cookbook so the user can flip through
  // recipes without bouncing back to the cookbook page each time.
  const [neighbours, setNeighbours] = useState({ prev: null, next: null, position: 0, total: 0 });

  useEffect(() => {
    let cancelled = false;
    setLoadError(null);
    const freshKey = `rf_fresh_${id}`;
    const bust = sessionStorage.getItem(freshKey);
    if (bust) sessionStorage.removeItem(freshKey);
    api.get(bust ? `/recipes/${id}?fresh=${Date.now()}` : `/recipes/${id}`).then((r) => {
      if (cancelled) return;
      setRecipe(r.data);
      setOverrideServes(r.data.serves);
    }).catch((err) => {
      if (cancelled) return;
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail || err?.message || "";
      if (status === 401 || status === 403) {
        setLoadError({ code: status, message: "You need to sign in to view this recipe." });
      } else if (status === 404) {
        setLoadError({ code: 404, message: "This recipe couldn't be found." });
      } else {
        setLoadError({ code: status || 0, message: detail || "Couldn't load this recipe. Please try again." });
      }
      // Toast is redundant now that we render a proper error state, but keep
      // for consistency with older behaviour.
      toast.error("Recipe not found");
    });
    api.get(`/recipes/${id}/neighbours`).then((r) => { if (!cancelled) setNeighbours(r.data); }).catch(() => {});
    return () => { cancelled = true; };
  }, [id]);

  // Auto-refresh the recipe on tab focus / visibility change so cross-
  // device edits and co-editor changes appear without a manual reload.
  // Example: cook edits a recipe on their phone, returns to the laptop
  // tab, the new ingredients/instructions/photo appear silently.
  //
  // Deliberately NO periodic poll here — a recipe view is a single
  // document that rarely changes; polling would burn bandwidth for no
  // benefit. Focus + visibility events cover the "came back to tab" case
  // which is the only realistic sync moment.
  //
  // Toast only fires when `updated_at` actually moved (so the cook knows
  // "something changed" rather than seeing a silent state flip). Skipped
  // entirely on the first refresh after mount to avoid a spurious toast.
  useEffect(() => {
    if (!id) return undefined;
    let cancelled = false;

    const refresh = async () => {
      try {
        // `?fresh=…` tells the service worker to skip its stale-while-
        // revalidate cache for THIS request and go straight to network.
        // Without this we'd get the previously-cached copy of the recipe
        // and never surface cross-device edits until the SW's background
        // revalidation happens on the NEXT poll.
        const { data } = await api.get(`/recipes/${id}?fresh=${Date.now()}`);
        if (cancelled) return;
        // Compare `updated_at` against whatever we currently render — if
        // it moved, the doc was edited elsewhere. Use a functional
        // setState so we always compare against the freshest local value,
        // not a stale closure snapshot.
        setRecipe((prev) => {
          if (prev && data.updated_at && prev.updated_at && data.updated_at !== prev.updated_at) {
            toast.success("Recipe updated ✨", {
              description: "Latest edits synced from another device or co-editor.",
              duration: 4000,
            });
          }
          return data;
        });
      } catch { /* silent — 404/401 already surfaced by primary loader */ }
    };

    const onVisibility = () => {
      if (document.visibilityState === "visible") refresh();
    };
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("focus", refresh);
    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("focus", refresh);
    };
  }, [id]);

  useEffect(() => {
    if (user) {
      api.get("/cookbooks").then((r) => {
        // Merge owned + co-edited cookbooks so the Move/Copy dropdowns
        // include books a friend has shared with the user AND granted
        // edit rights on. View-only invites are excluded (can't add
        // recipes to them anyway, so listing them would just 403).
        const owned = r.data.owned || [];
        const coEdited = r.data.co_edited || [];
        setCookbooks([...owned, ...coEdited]);
      });
    }
  }, [user]);

  useEffect(() => {
    if (recipe && overrideServes) {
      setScale(overrideServes / recipe.serves);
    }
  }, [overrideServes, recipe]);

  // timer tick — beep + browser notification on completion
  useEffect(() => {
    const id = setInterval(() => {
      setTimers((arr) => arr.map((t) => {
        if (t.remaining > 0) {
          const r = t.remaining - 1;
          if (r === 0 && !t.alerted) {
            playBeep(3);
            try { if (navigator.vibrate) navigator.vibrate([300, 120, 300, 120, 300]); } catch {}
            try {
              if ("Notification" in window && Notification.permission === "granted") {
                new Notification("Timer done", { body: t.label, silent: false });
              }
            } catch {}
            toast.success(`Timer done — ${t.label}`);
            return { ...t, remaining: 0, alerted: true };
          }
          return { ...t, remaining: r };
        }
        return t;
      }));
    }, 1000);
    return () => clearInterval(id);
  }, []);

  // refs so the long-lived recognizer always sees fresh state
  const recipeRef = useRef(recipe);
  const stepIdxRef = useRef(stepIdx);
  const ttsRef = useRef(ttsOn);
  useEffect(() => { recipeRef.current = recipe; }, [recipe]);
  useEffect(() => { stepIdxRef.current = stepIdx; }, [stepIdx]);
  useEffect(() => { ttsRef.current = ttsOn; }, [ttsOn]);

  // URL upgrade: if the visitor arrived on a bare-ID URL (/r/rcp_xxx) or an
  // out-of-date slug (title was renamed since Google indexed it), silently
  // rewrite the browser bar to the current /r/{slug}-{id} form once we know
  // the title. Uses history.replaceState so we don't push a new history entry
  // — the back button still returns them to wherever they came from.
  //
  // Why this matters:
  //   1. Users who share the URL from their address bar share the pretty one.
  //   2. Analytics logs the canonical URL instead of a mix of slug variants.
  //   3. Combined with <link rel="canonical"> this is Google's recommended
  //      "canonicalise on the client" pattern when a server-side 301 isn't
  //      practical for SPA routes.
  useEffect(() => {
    if (!recipe || !slugId) return;
    const target = recipePath(recipe); // e.g. "/r/grandmas-cake-rcp_abc"
    if (`/r/${slugId}` !== target) {
      const qs = window.location.search || "";
      const hash = window.location.hash || "";
      try { window.history.replaceState(window.history.state, "", `${target}${qs}${hash}`); } catch {}
    }
  }, [recipe, slugId]);

  // Warm up TTS voices once
  useEffect(() => { warmUpVoices(); }, []);

  // voice — long-lived recognizer that auto-restarts on silence ("no-speech" timeouts)
  useEffect(() => {
    if (!voiceOn) return;
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { toast.error("Speech recognition not supported in this browser"); setVoiceOn(false); return; }
    let stopped = false;
    const rec = new SR();
    rec.continuous = true;
    rec.interimResults = false;
    rec.lang = "en-US";
    rec.onresult = (e) => {
      const txt = Array.from(e.results).map((r) => r[0].transcript.toLowerCase()).join(" ");
      const steps = recipeRef.current?.instructions || [];
      if (/next( step)?|forward/.test(txt)) {
        setStepIdx((i) => {
          const next = Math.min(i + 1, steps.length - 1);
          if (ttsRef.current && steps[next]) speak(steps[next]);
          return next;
        });
      } else if (/back|previous|go back/.test(txt)) {
        setStepIdx((i) => {
          const prev = Math.max(i - 1, 0);
          if (ttsRef.current && steps[prev]) speak(steps[prev]);
          return prev;
        });
      } else if (/repeat|read it again|read again|say again/.test(txt)) {
        if (steps[stepIdxRef.current]) speak(steps[stepIdxRef.current]);
      } else if (/read off|stop reading/.test(txt)) {
        setTtsOn(false); speak("Reading turned off.");
      } else if (/read on|start reading/.test(txt)) {
        setTtsOn(true); speak("Reading turned on.");
      } else if (/stop listening|voice off|mute/.test(txt)) {
        setVoiceOn(false);
      }
    };
    // 'no-speech' / 'aborted' / 'audio-capture' errors are normal — just restart.
    rec.onerror = (e) => {
      if (e.error === "not-allowed" || e.error === "service-not-allowed") {
        toast.error("Microphone permission denied — enable mic for this site to use voice.");
        setVoiceOn(false);
      }
    };
    rec.onend = () => { if (!stopped) { try { rec.start(); } catch {} } };
    try { rec.start(); } catch {}
    return () => { stopped = true; try { rec.stop(); } catch {} };
  }, [voiceOn]);

  // Request notification permission once when voice/TTS toggled
  useEffect(() => {
    if ((voiceOn || ttsOn) && "Notification" in window && Notification.permission === "default") {
      try { Notification.requestPermission(); } catch {}
    }
  }, [voiceOn, ttsOn]);

  const launchTimer = (label, seconds) => {
    const id = `t_${Date.now()}_${Math.random()}`;
    setTimers((arr) => [...arr, { id, label, remaining: seconds, total: seconds, alerted: false }]);
  };

  const fetchSubstitutes = async (ingredientName) => {
    setSubOpen(ingredientName);
    setSubLoading(true);
    setSubData(null);
    try {
      const { data } = await api.post("/ai/substitute", {
        ingredient: ingredientName,
        recipe_title: recipe?.title || "",
      });
      setSubData(data);
    } catch {
      toast.error("Substitute AI is napping — try again.");
    } finally { setSubLoading(false); }
  };

  const uploadAudioMemory = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("audio/")) { toast.error("Please choose an audio file"); return; }
    if (file.size > 6 * 1024 * 1024) { toast.error("Audio must be under 6MB (≈60s)"); return; }
    setAudioBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data: up } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.post(`/recipes/${id}/audio-memory`, { url: up.url, title: file.name.replace(/\.[^.]+$/, "").slice(0, 80) });
      const { data: r } = await api.get(`/recipes/${id}`);
      setRecipe(r);
      toast.success("Audio memory saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally { setAudioBusy(false); if (audioFileRef.current) audioFileRef.current.value = ""; }
  };

  const removeAudioMemory = async () => {
    if (!window.confirm("Remove this audio memory?")) return;
    setAudioBusy(true);
    try {
      await api.delete(`/recipes/${id}/audio-memory`);
      const { data: r } = await api.get(`/recipes/${id}`);
      setRecipe(r);
      toast.success("Removed");
    } catch { toast.error("Could not remove"); }
    finally { setAudioBusy(false); }
  };

  // Live audio recording — MediaRecorder, capped at 60s.
  // We upload the recording on stop using the same flow as a file upload so
  // it lands in the existing audio_memories backend without new API surface.
  const startRecording = async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      toast.error("Recording isn't supported in this browser.");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      // Prefer webm/opus — Safari falls back to its default mp4/aac.
      let mime = "audio/webm;codecs=opus";
      if (!window.MediaRecorder?.isTypeSupported?.(mime)) {
        mime = MediaRecorder.isTypeSupported?.("audio/mp4") ? "audio/mp4" : "";
      }
      const rec = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      recChunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data?.size) recChunksRef.current.push(e.data); };
      rec.onstop = async () => {
        // Always stop the mic — otherwise Chrome shows the red recording dot.
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(recChunksRef.current, { type: rec.mimeType || "audio/webm" });
        if (!blob.size) { toast.error("Nothing recorded"); return; }
        const ext = (rec.mimeType || "audio/webm").includes("mp4") ? "m4a" : "webm";
        const file = new File([blob], `voice-memory-${Date.now()}.${ext}`, { type: blob.type });
        await uploadAudioMemory(file);
      };
      mediaRecRef.current = rec;
      setRecordSeconds(0);
      setRecording(true);
      rec.start();
      // Tick every second; auto-stop at 60s.
      recTimerRef.current = setInterval(() => {
        setRecordSeconds((s) => {
          const next = s + 1;
          if (next >= 60) {
            // schedule stop on next tick to let state settle
            setTimeout(() => stopRecording(), 0);
          }
          return next;
        });
      }, 1000);
    } catch (e) {
      toast.error(e?.name === "NotAllowedError" ? "Microphone permission denied" : "Could not start recording");
    }
  };

  const stopRecording = () => {
    try {
      if (recTimerRef.current) { clearInterval(recTimerRef.current); recTimerRef.current = null; }
      const rec = mediaRecRef.current;
      if (rec && rec.state !== "inactive") rec.stop();
    } catch { /* ignore */ }
    setRecording(false);
  };

  // Cleanup any in-flight recording on unmount
  useEffect(() => () => {
    if (recTimerRef.current) clearInterval(recTimerRef.current);
    try { if (mediaRecRef.current?.state === "recording") mediaRecRef.current.stop(); } catch {}
  }, []);

  // "Set as Recipe Image" — prepends the chosen scan URL to recipe.images
  // so the cleaned-up recipe page leads with grandma's original handwriting.
  const setScanAsRecipeImage = async (url) => {
    if (!url) return;
    setScanBusy(true);
    try {
      const next = [url, ...(recipe.images || []).filter((u) => u !== url)];
      await api.patch(`/recipes/${id}`, { images: next });
      const { data } = await api.get(`/recipes/${id}`);
      setRecipe(data);
      toast.success("Set as recipe image");
      setScanModalIdx(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't set image");
    } finally { setScanBusy(false); }
  };

  const fmt = (s) => `${Math.floor(s / 60).toString().padStart(2, "0")}:${(s % 60).toString().padStart(2, "0")}`;

  const fetchPairings = async () => {
    if (!recipe) return;
    setPairingsLoading(true);
    try {
      const { data } = await api.post("/ai/pairings", {
        title: recipe.title,
        ingredients_summary: recipe.ingredients.slice(0, 8).map((i) => `${i.quantity} ${i.unit} ${i.name}`).join(", "),
      });
      setPairings(data);
    } catch {
      toast.error("Pairing AI is napping — try again.");
    } finally { setPairingsLoading(false); }
  };

  const copyTo = async (cb) => {
    try {
      const { data } = await api.post(`/recipes/${id}/copy`, { target_cookbook_id: cb });
      toast.success("Recipe copied!");
      setCopyOpen(false);
      nav(recipePath(data));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Copy failed");
    }
  };

  const moveTo = async (cb) => {
    try {
      await api.post(`/recipes/${id}/move`, { target_cookbook_id: cb });
      toast.success("Recipe moved");
      setMoveOpen(false);
      // Refresh recipe so the new cookbook_id reflects in any breadcrumbs
      const r = await api.get(`/recipes/${id}`);
      setRecipe(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Move failed");
    }
  };

  const rate = async (n) => {
    try { await api.post(`/recipes/${id}/rate`, { stars: n }); toast.success("Thanks for rating!"); api.get(`/recipes/${id}`).then((r) => setRecipe(r.data)); } catch {}
  };

  const addNote = async () => {
    if (!note.trim()) return;
    try {
      await api.post(`/recipes/${id}/notes`, { content: note });
      setNote("");
      const { data } = await api.get(`/recipes/${id}`);
      setRecipe(data);
    } catch { toast.error("Could not add note"); }
  };

  const recipeUrl = useMemo(() => (typeof window !== "undefined" ? `${window.location.origin}${recipePath(recipe || id, recipe?.title)}` : ""), [id, recipe]);

  if (!recipe) {
    const netFail = loadError && ![401, 403, 404].includes(loadError.code);
    if ((!loadError || netFail) && hasSsr(window.location.pathname)) return <SsrFallback pathname={window.location.pathname} />;
    // Full-page loading / error state — replaces the near-invisible
    // `Loading…` that made a failed fetch look like a "blank white screen"
    // on iOS Safari after Sandbox → Save.
    return (
      <div className="min-h-[70vh] flex flex-col items-center justify-center bg-rf-cream px-6 py-16 text-center" data-testid="recipe-view-status">
        {/* When the recipe genuinely doesn't exist (deleted / private),
            tell Google explicitly via noindex + a "404 — Recipe not
            found" title so Search Console reclassifies these pages
            from "Soft 404" to "Not Found". The SPA still returns a
            200 OK HTTP status (we can't set 404 without SSR), but
            noindex + semantic content is Google's documented fix for
            SPA soft-404s. */}
        {loadError && loadError.code === 404 && (
          <PageMeta
            title="Recipe not found — RecipeFiler"
            description="This recipe no longer exists on RecipeFiler. It may have been deleted or set to private."
            noindex={true}
          />
        )}
        {loadError ? (
          <div className="max-w-md w-full">
            <div className="text-5xl mb-4" aria-hidden>🍽️</div>
            <h2 className="font-display font-black text-2xl text-rf-ink mb-2">
              {loadError.code === 401 || loadError.code === 403
                ? "Sign in to view this recipe"
                : loadError.code === 404
                  ? "Recipe not found"
                  : "Couldn't load this recipe"}
            </h2>
            <p className="text-rf-ink-soft mb-6">{loadError.message}</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                type="button"
                data-testid="recipe-view-retry"
                onClick={() => { setLoadError(null); setRecipe(null); /* trigger reload */ window.location.reload(); }}
                className="btn-amber justify-center"
              >
                Try again
              </button>
              <Link
                to="/dashboard"
                data-testid="recipe-view-dashboard"
                className="btn-outline-ink justify-center"
              >
                Back to My Cookbooks
              </Link>
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-3">
            <div className="w-10 h-10 rounded-full border-4 border-rf-amber border-t-transparent animate-spin" aria-hidden />
            <p className="text-rf-ink-soft text-sm">Loading recipe…</p>
          </div>
        )}
      </div>
    );
  }

  const isOwner = user?.user_id === recipe.owner_id;
  const isAdmin = user?.role === "Admin" || user?.role === "SuperAdmin";
  const isSuperAdmin = user?.role === "SuperAdmin";
  const canEdit = isOwner || isAdmin;
  const avgRating = recipe.rating_count ? (recipe.rating_sum / recipe.rating_count).toFixed(1) : null;

  // If the user got here from a cookbook page (referrer path starts with
  // `/cb/`), forward that as `return_to` on the Edit URL so RecipeEdit
  // sends them back to the cookbook after save instead of the recipe view.
  // Falls back to a clean edit URL when the referrer isn't a cookbook.
  const editHref = (() => {
    const base = `${recipePath(recipe)}/edit`;
    try {
      const ref = document.referrer ? new URL(document.referrer) : null;
      if (ref && ref.origin === window.location.origin && ref.pathname.startsWith("/cb/")) {
        return `${base}?return_to=${encodeURIComponent(ref.pathname + ref.search)}`;
      }
    } catch { /* ignore malformed referrer */ }
    return base;
  })();

  // Fire the async AI image job + poll status. Same pattern as
  // RecipeEdit's `generateAiImage` so behaviour is identical, but this
  // entry point lives on the recipe view page so SuperAdmins can trigger
  // it in one click on any image-less recipe (was previously only reachable
  // via the recipe edit page).
  const quickGenerateAiImage = async () => {
    setGenImgLoading(true);
    try {
      const { data: kick } = await api.post(`/recipes/${recipe.recipe_id}/ai-image`);
      if (kick.ai_image_status !== "generating" && kick.ai_image_url) {
        setRecipe((r) => ({ ...r, ai_image_url: kick.ai_image_url, ai_image_active: true }));
        toast.success("AI image ready.");
        return;
      }
      // Poll status every 3s (up to ~5 min)
      for (let i = 0; i < 100; i++) {
        await new Promise((r) => setTimeout(r, 3000));
        const { data: s } = await api.get(`/recipes/${recipe.recipe_id}/ai-image/status`);
        if (s.ai_image_status === "ready" && s.ai_image_url) {
          // Auto-activate as the hero on the view page (SuperAdmin can still
          // toggle it off from the edit page).
          try {
            await api.patch(`/recipes/${recipe.recipe_id}`, { ai_image_active: true });
          } catch (_e) { void _e; }
          setRecipe((r) => ({
            ...r,
            ai_image_url: s.ai_image_url,
            ai_image_active: true,
            ai_image_generated_at: s.ai_image_generated_at,
          }));
          toast.success("AI image ready and set as the hero.");
          return;
        }
        if (s.ai_image_status === "failed") {
          throw new Error(s.ai_image_error || "AI image generation failed. Try again in a minute.");
        }
      }
      throw new Error("AI image is taking longer than expected. Please refresh in a minute.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "AI image generation failed.");
    } finally {
      setGenImgLoading(false);
    }
  };

  return (
    <div className="bg-paper">
      {/* PageMeta gives us <title>, <meta description>, OG basics for the
          browser tab and social previews. RecipeJsonLd below sets the
          authoritative canonical + Recipe schema + refined OG (with the
          recipe's hero image). Private recipes are noindexed so drafts
          never leak into Google. */}
      <PageMeta
        title={recipe.seo_title || recipe.title || "Recipe"}
        description={buildRecipeMetaDescription(recipe)}
        noindex={!recipe.is_public}
      />
      {/* JSON-LD + canonical + OG meta — only injected for public recipes so
          private ones never leak into Google's index. */}
      <RecipeJsonLd recipe={recipe} />
      {/* Breadcrumb schema so Google shows "Directory › Recipe" trail
          under the search result URL (only for public recipes so private
          ones never leak into indexes). */}
      {/* Header */}
      <div className="max-w-5xl mx-auto px-6 md:px-10 pt-12 pb-6 no-print">
        {(() => {
          const params = new URLSearchParams(window.location.search);
          const fromParam = params.get("from");
          const cameFromDirectory = fromParam === "directory"
            || (typeof document !== "undefined" && document.referrer && /\/directory(\?|$|\/)/.test(document.referrer));
          const backTo = cameFromDirectory ? "/directory" : "/dashboard";
          const backLabel = cameFromDirectory ? "Directory" : "Dashboard";
          return (
            <Link
              to={backTo}
              data-testid="recipe-back-link"
              className="text-sm font-bold text-rf-orange hover:underline"
            >← {backLabel}</Link>
          );
        })()}
      </div>

      <article className="max-w-5xl mx-auto px-6 md:px-10 pb-20">
        {/* Prev / Next nav — flips through siblings in the same cookbook.
            Hidden when there's no neighbour on either side so we don't show
            empty bars on a single-recipe cookbook. */}
        {(neighbours.prev || neighbours.next) && (
          <div className="flex items-center justify-between gap-3 mb-8 no-print" data-testid="recipe-pager">
            {neighbours.prev ? (
              <Link
                to={recipePath(neighbours.prev)}
                data-testid="recipe-prev"
                className="group flex items-center gap-3 px-5 py-3 rounded-2xl border-2 border-rf-ink/10 bg-paper hover:border-rf-orange hover:bg-rf-amber/15 transition-all flex-1 min-w-0"
              >
                <span className="shrink-0 w-10 h-10 rounded-full bg-rf-ink text-rf-cream flex items-center justify-center font-bold text-lg group-hover:bg-rf-orange transition-colors">←</span>
                <span className="text-left min-w-0">
                  <span className="block text-[10px] uppercase tracking-wider text-rf-ink-soft font-bold">Previous</span>
                  <span className="block font-display font-extrabold text-rf-ink truncate">{neighbours.prev.title || "Untitled"}</span>
                </span>
              </Link>
            ) : <div className="flex-1" />}
            {neighbours.total > 0 && (
              <span className="hidden sm:inline-block text-xs font-bold text-rf-ink-soft uppercase tracking-wider px-3">
                Recipe {neighbours.position} of {neighbours.total}
              </span>
            )}
            {neighbours.next ? (
              <Link
                to={recipePath(neighbours.next)}
                data-testid="recipe-next"
                className="group flex items-center gap-3 px-5 py-3 rounded-2xl border-2 border-rf-ink/10 bg-paper hover:border-rf-orange hover:bg-rf-amber/15 transition-all flex-1 min-w-0 justify-end"
              >
                <span className="text-right min-w-0">
                  <span className="block text-[10px] uppercase tracking-wider text-rf-ink-soft font-bold">Next</span>
                  <span className="block font-display font-extrabold text-rf-ink truncate">{neighbours.next.title || "Untitled"}</span>
                </span>
                <span className="shrink-0 w-10 h-10 rounded-full bg-rf-ink text-rf-cream flex items-center justify-center font-bold text-lg group-hover:bg-rf-orange transition-colors">→</span>
              </Link>
            ) : <div className="flex-1" />}
          </div>
        )}

        {/* Hero image: prefer AI-generated headline when the owner has
            toggled it active, else fall back to the first uploaded photo.
            Final fallback = branded "Recipe Filer" default so image-less
            recipes still look intentional (and Google Rich Results still
            get a valid `image` field — matches the JSON-LD fallback in
            RecipeJsonLd.jsx / backend public.render_recipe_html). */}
        {(recipe.ai_image_active && recipe.ai_image_url) ? (
          <img
            src={recipe.ai_image_url}
            alt={`${recipe.title} — AI-generated headline${recipe.creator_name ? ` for ${recipe.creator_name}` : ""}`}
            className="w-full aspect-[16/9] object-cover rounded-3xl mb-10"
            data-testid="recipe-hero-ai-image"
            onError={(e) => {
              if (!e.currentTarget.dataset.fallback) {
                e.currentTarget.dataset.fallback = "1";
                e.currentTarget.src = "/recipe-default.jpg";
              }
            }}
          />
        ) : recipe.images?.[0] ? (
          <img
            src={recipe.images[0]}
            alt={`${recipe.title}${recipe.creator_name ? ` — recipe by ${recipe.creator_name}` : ""}${recipe.section ? `, ${recipe.section}` : ""}`}
            className="w-full aspect-[16/9] object-cover rounded-3xl mb-10"
            // Instagram / social CDN images expire; fall back gracefully
            // to the branded default so the hero never looks broken.
            onError={(e) => {
              if (!e.currentTarget.dataset.fallback) {
                e.currentTarget.dataset.fallback = "1";
                e.currentTarget.src = "/recipe-default.jpg";
              }
            }}
          />
        ) : (
          <div className="relative mb-10 group">
            <img
              src="/recipe-default.jpg"
              alt={`${recipe.title} — Recipe Filer branded placeholder (no photo uploaded yet)`}
              className="w-full aspect-[16/9] object-cover object-center rounded-3xl"
              data-testid="recipe-hero-default"
            />
            {/* NOTE: the owner "Add a photo" pill used to live here but was
                removed 2026-08-14 — users mistook it for an active upload
                button. Owners can still add a photo via the Edit screen's
                prominent "+ Add photo" button. */}
            {/* SuperAdmin quick-action — one-click AI hero on any
                image-less recipe. Never surfaces to normal users. */}
            {isSuperAdmin && (
              <button
                type="button"
                data-testid="recipe-hero-generate-ai"
                onClick={quickGenerateAiImage}
                disabled={genImgLoading}
                className="absolute bottom-4 left-4 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-rf-orange text-white text-sm font-bold shadow-lg hover:bg-rf-orange/85 transition-colors disabled:opacity-60"
                title="Generate an AI headline image (SuperAdmin)"
              >
                {genImgLoading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Generating…
                  </>
                ) : (
                  <>
                    <Sparkle size={16} weight="fill" />
                    AI image
                  </>
                )}
              </button>
            )}
          </div>
        )}

        <div className="flex flex-wrap items-start justify-between gap-6 mb-8">
          <div className="flex-1 min-w-[260px]">
            <div className="flex gap-2 mb-4">
              <div className="chip">{recipe.difficulty}</div>
              {recipe.is_public && <div className="chip">Public</div>}
              {recipe.is_public && recipe.copy_count > 0 && (
                <div className="chip !bg-rf-cream-alt !text-rf-ink inline-flex items-center gap-1" data-testid="recipe-copy-count">
                  <CopyIcon size={12} weight="bold" /> Copied {recipe.copy_count} {recipe.copy_count === 1 ? "time" : "times"}
                </div>
              )}
              {recipe.is_secret && <div className="chip bg-rf-ink/10 text-rf-ink">Secret</div>}
            </div>
            <h1 className="font-display text-4xl md:text-6xl lg:text-7xl font-black text-rf-ink leading-[0.95] mb-3">{recipe.title}</h1>
            <p className="text-rf-ink-soft text-lg">by {recipe.creator_name || "Anonymous"}{recipe.creation_year ? ` • ${recipe.creation_year}` : ""}</p>
            {isOwner && recipe.copy_state === "unedited" && (
              <p data-testid="recipe-unedited-copy-note" className="mt-2 text-sm text-rf-ink-soft bg-rf-amber/15 border border-rf-amber/40 rounded-xl px-3 py-2">
                This is a private copy from the directory. Make any change to it and it becomes your own recipe with your name on it, which you can then share.
              </p>
            )}
            <DietBadges recipe={recipe} canEdit={canEdit} signedIn={!!user} cookbooks={cookbooks} onChanged={(fresh) => setRecipe((r) => ({ ...r, ...fresh }))} />
            {recipe.is_public && recipe.owner_id && (
              <div className="mt-2">
                <CookLevelBadge userId={recipe.owner_id} />
              </div>
            )}
            {recipe.source_url && (
              <p className="text-xs text-rf-ink-soft mt-2" data-testid="recipe-source-badge">
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-rf-amber/15 text-rf-orange font-bold">
                  ↳ Imported from{" "}
                  <a href={recipe.source_url} target="_blank" rel="noreferrer noopener" className="underline">
                    {recipe.source_name || recipe.source_type || "the web"}
                  </a>
                </span>
              </p>
            )}
          </div>

          {/* "About this recipe" card. AI drafts a 30-word blurb but the
              owner (or an admin) can rewrite it — up to 500 words — via
              the pencil icon. Non-owners see the read-only text; if there's
              no summary at all AND the viewer can edit, a subtle "Add a
              summary" prompt lets them start one. */}
          <RecipeSummaryCard
            recipe={recipe}
            canEdit={canEdit}
            onSaved={(next, meta) => setRecipe((r) => {
              if (meta?.refetched) return { ...r, ...meta.refetched };
              if (meta?.restored) return { ...r, summary: next, summary_prior: null };
              return { ...r, summary: next };
            })}
          />

          {/* Non-English scan: offer a one-tap translation to UK English.
              Only visible to users who can edit this recipe (owner / co-
              editor / admin). After translating, we flip to a "Restore
              original" affordance so cooks can swap back any time. */}
          <TranslateCard
            recipe={recipe}
            canEdit={canEdit}
            onChanged={(fresh) => setRecipe((r) => ({ ...r, ...fresh }))}
          />


          <div className="flex flex-wrap gap-2 no-print">
            <HeartButton kind="recipe" id={recipe.recipe_id} count={recipe.favourite_count} variant="pill" />
            <button data-testid="recipe-kitchen-view" onClick={() => nav(`/k/${id}`)} className="btn-amber !py-2.5 !px-4 text-sm"><ForkKnife size={16} /> Kitchen View</button>
            <button
              data-testid="recipe-print"
              onClick={() => {
                // If the recipe has a video but no still image, prompt before printing —
                // a printed page can't show a video so we'd leave a blank space otherwise.
                const needsImage = Boolean(recipe.video_url) && (!recipe.images || recipe.images.length === 0);
                if (needsImage) {
                  setPrintGuardOpen(true);
                  return;
                }
                // Call window.print() synchronously within the click handler — modern
                // browsers (Safari, Firefox, Chrome) block print dialogs invoked from
                // setTimeout/async because they break the user-gesture chain. The old
                // toast+setTimeout pattern silently failed for any non-admin who hadn't
                // pre-allowed popups on the site.
                window.print();
              }}
              className="btn-outline-ink !py-2.5 !px-4 text-sm"
            ><Printer size={16} /> Print</button>
            <button data-testid="recipe-qr" onClick={() => setShowQR(true)} className="btn-outline-ink !py-2.5 !px-4 text-sm"><QrCode size={16} /> QR</button>
            <ShareRecipeButton recipe={recipe} url={recipeUrl} className="!py-2.5 !px-4 text-sm" />
            {!isOwner && recipe.is_public && !recipe.is_secret && <CopyToCookbookButton recipe={recipe} />}
            {canEdit && <button data-testid="recipe-edit" onClick={() => nav(editHref)} className="btn-outline-ink !py-2.5 !px-4 text-sm"><PencilSimple size={16} /> Edit</button>}
            {isOwner && cookbooks.length > 1 && (
              <div className="relative">
                <button data-testid="recipe-move-btn" onClick={() => { setMoveOpen(!moveOpen); setCopyOpen(false); }} className="btn-outline-ink !py-2.5 !px-4 text-sm"><Books size={16} /> Move to…</button>
                {moveOpen && (
                  <div className="absolute right-0 mt-2 bg-white rounded-xl shadow-xl border border-rf-ink/10 min-w-[220px] p-2 z-10">
                    {cookbooks.filter((c) => c.cookbook_id !== recipe.cookbook_id).map((c) => (
                      <button key={c.cookbook_id} data-testid={`recipe-move-to-${c.cookbook_id}`} onClick={() => moveTo(c.cookbook_id)} className="block w-full text-left px-3 py-2 hover:bg-rf-cream-alt rounded-lg text-sm font-semibold">
                        {c.title}{c.is_system ? <span className="text-xs text-rf-ink-soft ml-2">(Inbox)</span> : null}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
            {isOwner && cookbooks.length > 1 && (
              <div className="relative">
                <button onClick={() => { setCopyOpen(!copyOpen); setMoveOpen(false); }} className="btn-outline-ink !py-2.5 !px-4 text-sm"><CopyIcon size={16} /> Copy to…</button>
                {copyOpen && (
                  <div className="absolute right-0 mt-2 bg-white rounded-xl shadow-xl border border-rf-ink/10 min-w-[220px] p-2 z-10">
                    {cookbooks.filter((c) => c.cookbook_id !== recipe.cookbook_id).map((c) => (
                      <button key={c.cookbook_id} onClick={() => copyTo(c.cookbook_id)} className="block w-full text-left px-3 py-2 hover:bg-rf-cream-alt rounded-lg text-sm font-semibold">{c.title}</button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 md:grid-cols-4 gap-2 sm:gap-3 mb-2 [&>.card-recipe]:max-sm:!p-3">
          <div className="card-recipe !p-5 text-center">
            <Clock size={22} weight="duotone" className="mx-auto text-rf-orange mb-1" />
            <p className="text-xs text-rf-ink-soft">Prep</p>
            <p className="font-display font-black text-2xl text-rf-ink">
              {recipe.prep_minutes != null && recipe.prep_minutes !== "" ? `${recipe.prep_minutes}m` : "—"}
            </p>
          </div>
          <div className="card-recipe !p-5 text-center">
            <Clock size={22} weight="duotone" className="mx-auto text-rf-orange mb-1" />
            <p className="text-xs text-rf-ink-soft">Cook</p>
            <p className="font-display font-black text-2xl text-rf-ink">
              {recipe.cook_minutes != null && recipe.cook_minutes !== "" ? `${recipe.cook_minutes}m` : "—"}
            </p>
          </div>
          {/* Rest / cool / chill / marinate — only appears when non-zero so
              recipes without a rest phase don't get an empty "—" pill.
              The four-column grid still balances because Serves + Rating
              take the last two slots below. */}
          {recipe.rest_minutes != null && recipe.rest_minutes > 0 && (
            <div className="card-recipe !p-5 text-center">
              <Clock size={22} weight="duotone" className="mx-auto text-rf-orange mb-1" />
              <p className="text-xs text-rf-ink-soft">Rest</p>
              <p className="font-display font-black text-2xl text-rf-ink">{recipe.rest_minutes}m</p>
            </div>
          )}
          <div className="card-recipe !p-5 text-center">
            <Users size={22} weight="duotone" className="mx-auto text-rf-orange mb-1" />
            <p className="text-xs text-rf-ink-soft">Serves</p>
            <div className="flex items-center justify-center gap-0.5 sm:gap-1 sm:mt-1">
              <button onClick={() => setOverrideServes(Math.max(1, overrideServes - 1))} aria-label="Fewer servings" data-testid="recipe-serves-minus" className="w-8 h-8 sm:w-7 sm:h-7 shrink-0 rounded-full bg-rf-cream-alt font-bold">−</button>
              <span data-testid="recipe-serves-val" className="font-display font-black text-xl sm:text-2xl text-rf-ink min-w-[2ch] text-center">{overrideServes}</span>
              <button onClick={() => setOverrideServes(overrideServes + 1)} aria-label="More servings" data-testid="recipe-serves-plus" className="w-8 h-8 sm:w-7 sm:h-7 shrink-0 rounded-full bg-rf-cream-alt font-bold">+</button>
            </div>
          </div>
          <div className="card-recipe !p-5 text-center">
            <Star size={22} weight="duotone" className="mx-auto text-rf-orange mb-1" />
            <p className="text-xs text-rf-ink-soft">Rating</p>
            <p className="font-display font-black text-2xl text-rf-ink">{avgRating || "—"}</p>
          </div>
        </div>
        {/* Subtle total-time line — Prep + Cook only (Rest / marinate time
            is shown as its own pill and NOT rolled into "Total time" — users
            expect Total = the active time you'll spend in the kitchen). */}
        {(() => {
          const p = recipe.prep_minutes || 0, c = recipe.cook_minutes || 0;
          const filled = [recipe.prep_minutes, recipe.cook_minutes].filter((v) => v != null && v !== "").length;
          const total = p + c;
          if (filled < 2 || total <= 0) return <div className="mb-10" />;
          const hrs = Math.floor(total / 60);
          const mins = total % 60;
          const label = hrs ? `${hrs}h ${mins}m` : `${mins}m`;
          return (
            <p className="text-xs text-rf-ink-soft mb-10 text-center italic" data-testid="recipe-total-time">
              Total time · {label}
            </p>
          );
        })()}

        {/* Two col: ingredients / instructions */}
        <div className="grid lg:grid-cols-12 gap-10">
          <section className="lg:col-span-5 recipe-ingredients" aria-labelledby="recipe-ingredients-h">
            <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
              <h2 id="recipe-ingredients-h" className="overline">Ingredients</h2>
              <Link
                to="/kitchen-help"
                data-testid="recipe-kitchen-help-chip"
                className="no-print inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full bg-rf-amber/15 text-rf-orange hover:bg-rf-amber hover:text-rf-ink transition"
                title="Look up cooking terms and cuts"
              >
                Kitchen Help
              </Link>
            </div>
            <h2 className="font-display text-3xl font-black text-rf-ink mb-4">Shopping list</h2>
            {user && (
              <div className="flex flex-wrap items-start gap-3">
                <AddToShoppingListDrawer
                  recipe={recipe}
                  scale={scale}
                  cookbookSlug={recipe?.cookbook_slug || null}
                />
                <AddToPlanButton recipe={recipe} />
              </div>
            )}
            <ul className="space-y-3 mt-4">
              {recipe.ingredients.map((ing, i) => {
                const q = ing.quantity ? +(ing.quantity * scale).toFixed(2) : ing.quantity;
                return (
                  <li key={i} className="flex items-start gap-3 pb-3 border-b border-rf-ink/10">
                    <div className="w-2 h-2 rounded-full bg-rf-orange mt-2.5 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-rf-ink leading-snug">
                        {q ? <span className="font-bold">{q} {ing.unit} </span> : null}
                        <KitchenHelpLinkify text={ing.name} index={khIndex} />
                        {ing.notes && <span className="text-rf-ink-soft text-sm"> — {ing.notes}</span>}
                      </p>
                    </div>
                    <button
                      data-testid={`sub-btn-${i}`}
                      onClick={() => fetchSubstitutes(ing.name)}
                      title="Suggest substitutes"
                      className="text-rf-orange hover:text-rf-ink inline-flex items-center gap-1 text-xs font-bold shrink-0 no-print"
                    >
                      <Swap size={14} weight="bold" /> Sub
                    </button>
                  </li>
                );
              })}
            </ul>

            {recipe.tools?.length > 0 && (
              <div className="mt-10">
                <p className="overline mb-3">Tools</p>
                <ul className="text-rf-ink-soft space-y-1.5">
                  {recipe.tools.map((t, i) => <li key={i}>· {t}</li>)}
                </ul>
              </div>
            )}
          </section>

          <section className="lg:col-span-7 recipe-instructions" aria-labelledby="recipe-instructions-h">
            <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
              <h2 id="recipe-instructions-h" className="overline">Instructions</h2>
              <div className="flex gap-2 no-print">
                <button data-testid="tts-toggle" onClick={() => setTtsOn(!ttsOn)} className={`inline-flex items-center gap-2 text-xs font-bold px-3 py-1.5 rounded-full transition-all ${ttsOn ? "bg-rf-amber text-rf-ink" : "bg-rf-cream-alt text-rf-ink-soft"}`}>
                  <SpeakerHigh size={14} weight={ttsOn ? "fill" : "regular"} /> {ttsOn ? "Read steps" : "Read steps"}
                </button>
                <button data-testid="voice-toggle" onClick={() => setVoiceOn(!voiceOn)} className={`inline-flex items-center gap-2 text-xs font-bold px-3 py-1.5 rounded-full transition-all ${voiceOn ? "bg-rf-orange text-white" : "bg-rf-cream-alt text-rf-ink-soft"}`}>
                  {voiceOn ? <Microphone size={14} weight="fill" /> : <MicrophoneSlash size={14} />} {voiceOn ? "Listening…" : "Voice off"}
                </button>
              </div>
            </div>
            <h2 className="font-display text-3xl font-black text-rf-ink mb-2">Step by step</h2>
            {(voiceOn || ttsOn) && (
              <p className="text-xs text-rf-ink-soft mb-4 no-print">
                Say <b>"next"</b>, <b>"back"</b>, <b>"repeat"</b> or <b>"stop listening"</b>. Tap a step to hear it aloud.
              </p>
            )}
            <ol className="space-y-5">
              {recipe.instructions.map((step, i) => {
                const parts = parseTimers(step);
                return (
                  <li key={i} className={`flex gap-4 ${ttsOn ? "cursor-pointer" : ""}`} onClick={() => { if (ttsOn) speak(step); }}>
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center font-display font-black shrink-0 ${stepIdx === i ? "bg-rf-orange text-white" : "bg-rf-amber text-rf-ink"}`}>{i + 1}</div>
                    <p className="text-rf-ink leading-relaxed pt-1">
                      {parts.map((p, j) => p.kind === "text" ? <span key={j}>{p.value}</span> : <TimerChip key={j} {...p} onLaunch={launchTimer} />)}
                    </p>
                  </li>
                );
              })}
            </ol>

            {/* Per-serving nutrition card — rendered as the final element of
                the recipe body so cooks finish on cuisine detail rather than
                landing on macros before they've read a single step. Gated
                by the admin site flag `nutrition_estimates_enabled` so a
                single toggle in Admin > Site Config can hide the card
                across the whole public catalogue. */}
            {flags.nutrition_estimates_enabled && (
              <div className="mt-10">
                <NutritionCard
                  recipe={recipe}
                  canEdit={canEdit}
                  onChanged={(fresh) => setRecipe((r) => ({ ...r, ...fresh }))}
                />
              </div>
            )}
          </section>
        </div>

        {/* Active timers floating */}
        {timers.length > 0 && (
          <div className="fixed bottom-4 right-4 space-y-2 z-30 no-print">
            {timers.map((t) => (
              <div key={t.id} className="bg-rf-ink text-rf-cream rounded-2xl shadow-xl p-4 flex items-center gap-4 min-w-[220px]">
                <div className="w-12 h-12 rounded-full bg-rf-amber text-rf-ink font-display font-black flex items-center justify-center text-sm">{t.remaining <= 0 ? "DONE" : fmt(t.remaining)}</div>
                <div className="flex-1">
                  <p className="text-xs text-rf-cream/60">Timer</p>
                  <p className="font-bold capitalize">{t.label}</p>
                </div>
                <button onClick={() => setTimers((arr) => arr.filter((x) => x.id !== t.id))} className="text-rf-cream/60 hover:text-rf-amber">✕</button>
              </div>
            ))}
          </div>
        )}

        {/* Original scanned pages — preserved so grandma's actual page lives on
            forever alongside the cleaned-up recipe. Visible to anyone who can
            see the recipe; hidden in print to keep PDFs compact. Click a tile
            to zoom; owners can promote any page to the recipe's hero image. */}
        {recipe.scan_pages?.length > 0 && (
          <div className="mt-16 no-print" data-testid="recipe-scan-pages">
            <p className="overline mb-3">Original scan{recipe.scan_pages.length > 1 ? `s — ${recipe.scan_pages.length} pages` : ""}</p>
            <p className="text-sm text-rf-ink-soft mb-4 max-w-2xl">
              We&apos;ve kept the original page{recipe.scan_pages.length > 1 ? "s" : ""} so the handwriting (and the kitchen-stained edges) live alongside the cleaned-up recipe forever.
              {isOwner ? " Tap any page to zoom — or set it as the recipe's image." : " Tap any page to zoom."}
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {recipe.scan_pages.map((url, idx) => (
                <button
                  key={url}
                  type="button"
                  onClick={() => setScanModalIdx(idx)}
                  data-testid={`recipe-scan-page-${idx}`}
                  className="relative block group rounded-xl overflow-hidden border-2 border-rf-ink/10 hover:border-rf-orange transition-colors"
                >
                  <img src={url} alt={`Original handwritten ${recipe.title} recipe — page ${idx + 1}`} className="w-full aspect-[3/4] object-cover" loading="lazy" />
                  <span className="absolute top-1.5 left-1.5 bg-rf-ink text-rf-cream text-[10px] font-black px-2 py-0.5 rounded-md">Page {idx + 1}</span>
                  {recipe.images?.[0] === url && (
                    <span className="absolute top-1.5 right-1.5 bg-rf-orange text-white text-[10px] font-black px-2 py-0.5 rounded-md inline-flex items-center gap-1">
                      <ImageIcon size={10} weight="fill" /> Hero
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Tag chips + Related recipes — internal linking for SEO and an
            opportunity for cooks to discover more from the same cookbook
            or carrying similar tags. Public recipes only. */}
        {recipe.is_public && (recipe.tags?.length > 0 || (neighbours?.total || 0) > 1) && (
          <div className="mt-16 no-print border-t border-rf-ink/10 pt-10">
            {recipe.tags?.length > 0 && (
              <div className="mb-10" data-testid="recipe-tags">
                <p className="overline mb-3">Tagged</p>
                <div className="flex flex-wrap gap-2">
                  {recipe.cuisine && (
                    <Link
                      to={`/cuisine/${recipe.cuisine.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-rf-amber/30 hover:bg-rf-amber/50 text-rf-ink text-xs font-bold transition-colors"
                      data-testid="recipe-cuisine-link"
                    >
                      More {recipe.cuisine} recipes →
                    </Link>
                  )}
                  {recipe.tags.map((t) => (
                    <Link
                      key={t}
                      to={`/recipes/tag/${encodeURIComponent(t.toLowerCase().replace(/\s+/g, "-"))}`}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-rf-cream-alt hover:bg-rf-amber/20 text-rf-ink text-xs font-bold transition-colors"
                      data-testid={`recipe-tag-${t}`}
                    >
                      #{t}
                    </Link>
                  ))}
                </div>
              </div>
            )}
            <RelatedRecipes recipe={recipe} />
          </div>
        )}

        {/* Rating */}
        <div className="mt-16 card-recipe text-center no-print">
          <p className="overline mb-3">Rate this recipe</p>
          <div className="flex justify-center gap-2">
            {[1, 2, 3, 4, 5].map((n) => (
              <button key={n} data-testid={`rate-${n}`} onClick={() => rate(n)} className="text-rf-amber hover:text-rf-orange transition-colors">
                <Star size={36} weight="duotone" />
              </button>
            ))}
          </div>
        </div>

        {/* Shared notes */}
        <div className="mt-12 no-print">
          <p className="overline mb-3">Shared notes</p>
          <h2 className="font-display text-3xl font-black text-rf-ink mb-6">Cook's annotations</h2>
          <div className="space-y-3 mb-4">
            {(recipe.shared_notes || []).map((n) => (
              <div key={n.note_id} className="card-recipe !p-4">
                <p className="text-xs text-rf-orange font-bold mb-1">{n.user_name} · {new Date(n.created_at).toLocaleDateString()}</p>
                <p className="text-rf-ink-soft">{n.content}</p>
              </div>
            ))}
            {!(recipe.shared_notes || []).length && (
              <p className="text-rf-ink-soft text-sm">
                {isOwner ? "No notes yet — leave the first." : "No notes yet."}
              </p>
            )}
          </div>
          {/* Only the recipe owner can add Cook's annotations. Everyone else sees
              the read-only notes above. This stops random viewers from spamming
              someone else's recipe page. */}
          {user && isOwner && (
            <div className="flex gap-2">
              <input data-testid="note-input" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add a note for cooks…" className="input-rf" />
              <button data-testid="note-add" onClick={addNote} className="btn-outline-ink !py-3 !px-4 text-sm">Add</button>
            </div>
          )}
        </div>

        {/* Family Legacy Audio Memory */}
        <div className="mt-12 no-print">
          <p className="overline mb-3">Family legacy</p>
          <h2 className="font-display text-3xl font-black text-rf-ink mb-2">Audio reminiscence</h2>
          <p className="text-rf-ink-soft text-sm mb-5 max-w-xl">A short voice memory — Mum reading the recipe out, a grandparent sharing the story behind it, a tip whispered while cooking. Up to ~60 seconds.</p>
          {(recipe.audio_memories || []).length === 0 ? (
            isOwner ? (
              <div className="card-recipe !p-5 flex flex-wrap items-center gap-4">
                <Heart size={28} weight="duotone" className="text-rf-orange shrink-0" />
                <div className="flex-1 min-w-[180px]">
                  <p className="font-display font-extrabold text-rf-ink">Add a voice memory</p>
                  <p className="text-xs text-rf-ink-soft">Record up to 60 seconds in-app — or upload MP3 / M4A / WAV (≤6 MB).</p>
                </div>
                {!recording ? (
                  <button
                    data-testid="audio-mem-record"
                    onClick={startRecording}
                    disabled={audioBusy}
                    className="btn-amber !py-2.5 !px-4 text-sm"
                  >
                    <Microphone size={16} weight="fill" /> Record
                  </button>
                ) : (
                  <button
                    data-testid="audio-mem-stop"
                    onClick={stopRecording}
                    className="btn-amber !py-2.5 !px-4 text-sm !bg-red-600 hover:!bg-red-700"
                  >
                    <Stop size={16} weight="fill" /> Stop ({60 - recordSeconds}s)
                  </button>
                )}
                <button data-testid="audio-mem-upload" onClick={() => audioFileRef.current?.click()} disabled={audioBusy || recording} className="btn-outline-ink !py-2.5 !px-4 text-sm">
                  <UploadSimple size={16} /> {audioBusy ? "Uploading…" : "Upload"}
                </button>
                <input ref={audioFileRef} type="file" accept="audio/*" className="hidden" onChange={(e) => uploadAudioMemory(e.target.files?.[0])} />
              </div>
            ) : (
              <p className="text-rf-ink-soft text-sm italic">No audio memory yet.</p>
            )
          ) : (
            (recipe.audio_memories || []).map((m, i) => (
              <div key={i} className="card-recipe !p-5 flex flex-wrap items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-rf-amber/20 flex items-center justify-center shrink-0">
                  <Play size={22} weight="duotone" className="text-rf-orange" />
                </div>
                <div className="flex-1 min-w-[180px]">
                  <p className="font-display font-extrabold text-rf-ink">{m.title}</p>
                  <p className="text-xs text-rf-ink-soft">by {m.uploaded_by} · {new Date(m.uploaded_at).toLocaleDateString()}</p>
                </div>
                <audio data-testid="audio-mem-player" controls src={m.url} className="w-full sm:w-auto" />
                {isOwner && (
                  <button data-testid="audio-mem-remove" onClick={removeAudioMemory} disabled={audioBusy} className="text-xs font-bold text-red-600 hover:underline inline-flex items-center gap-1">
                    <Trash size={14} /> Remove
                  </button>
                )}
              </div>
            ))
          )}
        </div>

        {/* AI Pairings */}
        <div className="mt-16 card-recipe no-print">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="overline mb-1">Complete the table</p>
              <h2 className="font-display text-2xl font-black text-rf-ink">Smart wine & side pairings</h2>
            </div>
            <button data-testid="ai-pairings-btn" onClick={fetchPairings} disabled={pairingsLoading} className="btn-amber !py-2.5 !px-4 text-sm">
              <Sparkle size={16} /> {pairingsLoading ? "Thinking…" : "Suggest"}
            </button>
          </div>
          {pairings && (
            <div className="grid sm:grid-cols-2 gap-4 mt-4">
              {[
                ["Wines", pairings.wines],
                ["Beverages", pairings.beverages],
                ["Non-alcoholic", pairings.non_alcoholic],
                ["Sides", pairings.side_dishes],
              ].map(([label, items]) => (
                <div key={label} className="bg-rf-cream-alt rounded-2xl p-4">
                  <p className="overline mb-2">{label}</p>
                  <ul className="space-y-2 text-sm">
                    {(items || []).map((it, i) => (
                      <li key={i}>
                        <p className="font-bold text-rf-ink">{it.name}</p>
                        <p className="text-rf-ink-soft text-xs">{it.why}</p>
                      </li>
                    ))}
                    {!items?.length && <li className="text-rf-ink-soft text-xs">No suggestions.</li>}
                  </ul>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Version timeline */}
        {(recipe.versions?.length > 0) && (
          <div className="mt-16 no-print">
            <p className="overline mb-3">Evolution timeline</p>
            <h2 className="font-display text-2xl font-black text-rf-ink mb-6">How this recipe has changed</h2>
            <div className="space-y-3">
              {recipe.versions.map((v) => (
                <div key={v.version_id} className="card-recipe !p-4">
                  <p className="text-xs text-rf-orange font-bold mb-1">{v.label}</p>
                  <p className="text-sm text-rf-ink-soft">Edited {new Date(v.created_at).toLocaleString()}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </article>

      {/* Scan-page lightbox: zoom + (owner) promote to hero image */}
      {scanModalIdx !== null && recipe.scan_pages?.[scanModalIdx] && (
        <div
          className="fixed inset-0 z-50 bg-rf-ink/90 backdrop-blur-sm flex items-center justify-center p-4 sm:p-8"
          onClick={() => setScanModalIdx(null)}
          data-testid="scan-lightbox"
        >
          <div className="relative max-w-3xl w-full max-h-full flex flex-col" onClick={(e) => e.stopPropagation()}>
            <button
              data-testid="scan-lightbox-close"
              onClick={() => setScanModalIdx(null)}
              className="absolute -top-2 -right-2 sm:top-2 sm:right-2 z-10 w-10 h-10 rounded-full bg-white text-rf-ink shadow-xl flex items-center justify-center hover:bg-rf-amber transition-colors"
              aria-label="Close"
            >
              <CloseIcon size={20} weight="bold" />
            </button>
            <div className="bg-white rounded-3xl overflow-hidden shadow-2xl flex flex-col max-h-[90vh]">
              <div className="flex-1 overflow-auto">
                <img
                  src={recipe.scan_pages[scanModalIdx]}
                  alt={`Original handwritten ${recipe.title} recipe — scanned page ${scanModalIdx + 1} of ${recipe.scan_pages.length}`}
                  className="w-full h-auto"
                  data-testid="scan-lightbox-img"
                />
              </div>
              <div className="p-4 sm:p-5 border-t border-rf-ink/10 bg-rf-cream-alt flex flex-wrap items-center gap-3">
                <p className="font-display font-extrabold text-rf-ink flex-1 min-w-[140px]">
                  Page {scanModalIdx + 1} of {recipe.scan_pages.length}
                </p>
                <a
                  href={recipe.scan_pages[scanModalIdx]}
                  target="_blank"
                  rel="noreferrer"
                  className="btn-outline-ink !py-2 !px-3 text-xs"
                >
                  <DownloadSimple size={14} /> Original
                </a>
                {isOwner && (
                  recipe.images?.[0] === recipe.scan_pages[scanModalIdx] ? (
                    <span className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-rf-amber/30 text-rf-orange text-xs font-bold">
                      <ImageIcon size={14} weight="fill" /> Currently the recipe image
                    </span>
                  ) : (
                    <button
                      data-testid="scan-set-as-image"
                      onClick={() => setScanAsRecipeImage(recipe.scan_pages[scanModalIdx])}
                      disabled={scanBusy}
                      className="btn-amber !py-2 !px-3 text-xs"
                    >
                      <ImageIcon size={14} weight="fill" /> {scanBusy ? "Setting…" : "Set as recipe image"}
                    </button>
                  )
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* QR overlay */}

      <PrintImageGuardModal
        open={printGuardOpen}
        recipes={[recipe]}
        onClose={() => setPrintGuardOpen(false)}
        onAllResolved={async () => {
          // Refetch the recipe so the printed page renders the freshly-uploaded image
          try {
            const { data } = await api.get(`/recipes/${id}`);
            setRecipe(data);
          } catch { /* fall through */ }
          setPrintGuardOpen(false);
          toast.success("Opening print dialog…");
          setTimeout(() => window.print(), 200);
        }}
      />


      {showQR && (
        <div className="fixed inset-0 z-50 bg-rf-ink/80 backdrop-blur-sm flex items-center justify-center p-6" onClick={() => setShowQR(false)}>
          <div className="bg-white rounded-3xl p-8 max-w-sm w-full text-center" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-display font-black text-2xl text-rf-ink mb-1">{recipe.title}</h3>
            <p className="text-xs text-rf-ink-soft mb-3">Scan or share</p>
            <div className="flex justify-center my-4">
              <div id="recipe-qr-canvas">
                <QRCodeCanvas value={recipeUrl} size={220} fgColor="#EF8200" bgColor="#FDFBF7" includeMargin />
              </div>
            </div>
            <p className="text-xs text-rf-ink-soft break-all mb-5">{recipeUrl}</p>
            <div className="grid grid-cols-3 gap-2 mb-3">
              <button
                data-testid="recipe-qr-download"
                onClick={() => {
                  const canvas = document.querySelector("#recipe-qr-canvas canvas");
                  if (!canvas) return;
                  const link = document.createElement("a");
                  link.download = `${(recipe.title || "recipe").replace(/\s+/g, "-").toLowerCase()}-qr.png`;
                  link.href = canvas.toDataURL("image/png");
                  link.click();
                  toast.success("QR downloaded");
                }}
                className="btn-outline-ink !py-2.5 !px-3 !text-xs"
              >
                <DownloadSimple size={14} /> PNG
              </button>
              <button
                data-testid="recipe-qr-copy"
                onClick={async () => { await navigator.clipboard.writeText(recipeUrl); toast.success("Link copied"); }}
                className="btn-outline-ink !py-2.5 !px-3 !text-xs"
              >
                <CopyIcon size={14} /> Copy
              </button>
              <button
                data-testid="recipe-qr-share"
                onClick={async () => {
                  if (navigator.share) {
                    try { await navigator.share({ title: recipe.title, text: `Check out this recipe: ${recipe.title}`, url: recipeUrl }); }
                    catch {}
                  } else {
                    await navigator.clipboard.writeText(recipeUrl);
                    toast.success("Link copied — paste to share");
                  }
                }}
                className="btn-outline-ink !py-2.5 !px-3 !text-xs"
              >
                <ShareNetwork size={14} /> Share
              </button>
            </div>
            <button onClick={() => setShowQR(false)} className="btn-amber w-full">Close</button>
          </div>
        </div>
      )}

      {/* Substitute modal */}
      {subOpen && (
        <div className="fixed inset-0 z-50 bg-rf-ink/80 backdrop-blur-sm flex items-center justify-center p-6" onClick={() => { setSubOpen(null); setSubData(null); }}>
          <div className="bg-white rounded-3xl p-8 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <p className="overline mb-2">AI substitute</p>
            <h3 className="font-display font-black text-2xl text-rf-ink mb-1">Swaps for {subOpen}</h3>
            <p className="text-rf-ink-soft text-sm mb-5">Practical kitchen alternatives with ratios and flavour notes.</p>
            {subLoading ? (
              <div className="py-8 text-center text-rf-ink-soft">
                <Sparkle size={28} weight="duotone" className="mx-auto text-rf-orange animate-pulse mb-2" />
                <p className="text-sm">Thinking…</p>
              </div>
            ) : (subData?.substitutions?.length ? (
              <ul className="space-y-4 mb-6">
                {subData.substitutions.map((s, i) => (
                  <li key={i} className="border-l-4 border-rf-amber pl-4">
                    <p className="font-display font-extrabold text-lg text-rf-ink">{s.name}</p>
                    <p className="text-xs font-bold text-rf-orange uppercase tracking-wider mb-1">{s.ratio}</p>
                    <p className="text-sm text-rf-ink-soft">{s.note}</p>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-rf-ink-soft text-sm py-6 text-center">No substitutes found. Try again.</p>
            ))}
            <button data-testid="sub-modal-close" onClick={() => { setSubOpen(null); setSubData(null); }} className="btn-ink w-full">Close</button>
          </div>
        </div>
      )}
    </div>
  );
}


/* RelatedRecipes — bottom-of-page widget that pulls in up to 4 sibling
 * public recipes from the SAME cookbook first, then fills the rest from
 * recipes that share at least one tag. Internal linking → cooks discover
 * more + Google has more in-content links to follow.
 *
 * Backend has no dedicated "related" endpoint so we fetch by-cookbook for
 * the same-cookbook tier and the public directory for the tag-based tier.
 * Both are cached friendly + already public-only.
 */
function RelatedRecipes({ recipe }) {
  const [related, setRelated] = useState([]);

  useEffect(() => {
    if (!recipe?.is_public) return;
    let cancelled = false;
    (async () => {
      const out = [];
      const seen = new Set([recipe.recipe_id]);
      // Same cookbook (public siblings only)
      try {
        const { data } = await api.get(`/recipes/by-cookbook/${recipe.cookbook_id}`);
        const siblings = (data || []).filter((r) => r.is_public && !seen.has(r.recipe_id));
        siblings.forEach((r) => { if (out.length < 4 && !seen.has(r.recipe_id)) { out.push(r); seen.add(r.recipe_id); } });
      } catch { /* ignore */ }
      // Tag-based fill
      if (out.length < 4 && recipe.tags?.length) {
        for (const tag of recipe.tags) {
          if (out.length >= 4) break;
          try {
            const { data } = await api.get(`/directory/tag/${encodeURIComponent(tag)}`);
            (data?.recipes || []).forEach((r) => {
              if (out.length < 4 && !seen.has(r.recipe_id)) { out.push(r); seen.add(r.recipe_id); }
            });
          } catch { /* ignore */ }
        }
      }
      if (!cancelled) setRelated(out);
    })();
    return () => { cancelled = true; };
  }, [recipe?.recipe_id, recipe?.cookbook_id, recipe?.is_public, recipe?.tags]);

  if (!related.length) return null;
  return (
    <div data-testid="related-recipes">
      <p className="overline mb-3">More like this</p>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {related.map((r) => (
          <Link
            key={r.recipe_id}
            to={recipePath(r)}
            data-testid={`related-recipe-${r.recipe_id}`}
            className="group block rounded-2xl overflow-hidden bg-white border border-rf-ink/10 hover:border-rf-orange transition-colors"
          >
            {((r.ai_image_active && r.ai_image_url) || r.images?.[0]) ? (
              <img src={(r.ai_image_active && r.ai_image_url) || r.images[0]} alt={`${r.title} — recipe by ${r.creator_name || "a Recipe Filer cook"}`} className="w-full aspect-[4/3] object-cover" loading="lazy" />
            ) : (
              <div className="w-full aspect-[4/3] bg-rf-cream-alt flex items-center justify-center">
                <BookOpenText size={32} className="text-rf-orange/40" />
              </div>
            )}
            <div className="p-4">
              <p className="font-display font-extrabold text-rf-ink text-sm leading-tight group-hover:text-rf-orange transition-colors">{r.title}</p>
              {r.creator_name && <p className="text-[11px] text-rf-ink-soft mt-1">by {r.creator_name}</p>}
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
